import React, { useState, useEffect } from 'react';

// SoKo Water Systems — Install Prompt
//
// Shows an "Add to Home Screen" prompt after the user has successfully
// loaded a tank reading. Tiered by platform:
//   - Chromium (Android Chrome/Edge, desktop): captures beforeinstallprompt
//     and fires the NATIVE install dialog on tap.
//   - iOS: no install API → animated Share → Add to Home Screen how-to.
//   - Android non-Chromium (Firefox, Samsung) / desktop w/o prompt:
//     animated browser-menu how-to (tier-3 fallback).
//   - In-app browsers (WhatsApp, Instagram, etc.): can't install at all →
//     lead with "open in your browser first", then the same steps.
//   - Already installed (standalone): hidden.
//   - Dismissed: hidden for 30 days (localStorage).

const DISMISS_KEY = 'soko-install-dismissed';
const DISMISS_DAYS = 30;

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Platform + environment detection. `inApp` is orthogonal to os.
function detectEnv() {
  const ua = navigator.userAgent || '';
  const inApp = /FBAN|FBAV|Instagram|Line\/|Twitter|WhatsApp|MicroMessenger|WeChat|Snapchat|Pinterest|GSA\//i.test(ua);
  if (/iphone|ipad|ipod/i.test(ua) && !window.MSStream) return { os: 'ios', inApp };
  if (/android/i.test(ua)) return { os: 'android', inApp };
  return { os: 'other', inApp };
}

function wasRecentlyDismissed() {
  const dismissedAt = localStorage.getItem(DISMISS_KEY);
  if (!dismissedAt) return false;
  const ageDays = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_DAYS;
}

// Copy + which animation to show, per environment. Deliberately says
// "browser" (never "Safari") so it reads right on every browser.
function instructionsFor(env) {
  if (env.os === 'ios') {
    return {
      variant: 'ios',
      steps: [
        <>Tap the <strong>Share</strong> button in your <strong>browser</strong></>,
        <>Choose <strong>Add to Home Screen</strong></>,
        <>Tap <strong>Add</strong> — done!</>,
      ],
    };
  }
  // android + other both use the overflow-menu animation
  return {
    variant: 'menu',
    steps: [
      <>Open your <strong>browser menu</strong> (the ⋮ or ••• icon)</>,
      <>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></>,
      <>Confirm to add — done!</>,
    ],
  };
}

// ── Animated how-to: iOS share sheet ─────────────────────────
function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M12 3v12M12 3l-4 4M12 3l4 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function AthIcon() {
  return (
    <svg className="itip-ath-ico" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="1.9" />
      <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function DemoIos() {
  return (
    <div className="itip-demo" aria-hidden="true">
      <div className="itip-bar">app.sokowatersystems.com</div>
      <div className="itip-page">
        <div className="itip-logo" />
        <div className="itip-ln s" /><div className="itip-ln m" /><div className="itip-ln m" /><div className="itip-ln s" />
      </div>
      <div className="itip-chrome">
        <svg viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        <svg viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        <span className="itip-share"><ShareIcon /></span>
        <svg viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" /></svg>
      </div>
      <div className="itip-sheet">
        <div className="itip-grip" />
        <div className="itip-list">
          <div className="itip-people"><span className="itip-circle" /><span className="itip-circle" /><span className="itip-circle" /><span className="itip-circle" /></div>
          <div className="itip-apps"><span className="itip-app" /><span className="itip-app" /><span className="itip-app" /><span className="itip-app" /></div>
          <div className="itip-row"><span className="itip-phln" style={{ width: '58%' }} /><span className="itip-phico" /></div>
          <div className="itip-row"><span className="itip-phln" style={{ width: '46%' }} /><span className="itip-phico" /></div>
          <div className="itip-row hl"><span>Add to Home Screen</span><AthIcon /></div>
          <div className="itip-row"><span className="itip-phln" style={{ width: '52%' }} /><span className="itip-phico" /></div>
        </div>
      </div>
      <div className="itip-finger itip-finger--ios" />
    </div>
  );
}

// ── Animated how-to: Android / other overflow menu ───────────
function DemoMenu() {
  return (
    <div className="itip-demo" aria-hidden="true">
      <div className="itip-bar">app.sokowatersystems.com</div>
      <span className="itip-topmenu">
        <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="1.6" fill="currentColor" /><circle cx="12" cy="12" r="1.6" fill="currentColor" /><circle cx="12" cy="19" r="1.6" fill="currentColor" /></svg>
      </span>
      <div className="itip-page">
        <div className="itip-logo" />
        <div className="itip-ln s" /><div className="itip-ln m" /><div className="itip-ln m" /><div className="itip-ln s" />
      </div>
      <div className="itip-menu">
        <div className="itip-mrow"><span className="itip-phln" style={{ width: '60%' }} /></div>
        <div className="itip-mrow"><span className="itip-phln" style={{ width: '48%' }} /></div>
        <div className="itip-mrow hl"><span>Add to Home screen</span><AthIcon /></div>
        <div className="itip-mrow"><span className="itip-phln" style={{ width: '54%' }} /></div>
      </div>
      <div className="itip-finger itip-finger--menu" />
    </div>
  );
}

export default function InstallPrompt({ ready }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [instructions, setInstructions] = useState(false);
  const [env] = useState(detectEnv);

  useEffect(() => {
    function handleBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;
    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, [ready]);

  function handleInstallClick() {
    if (deferredPrompt) {
      // Chromium: fire the real install dialog.
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null);
        setShow(false);
      });
    } else {
      // Everyone else: show the animated how-to (tier-3 fallback).
      setInstructions(true);
    }
  }

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShow(false);
    setInstructions(false);
  }

  // ── Instructions overlay ──
  if (instructions) {
    const { variant, steps } = instructionsFor(env);
    return (
      <div className="install-overlay" onClick={handleDismiss}>
        <div className="install-modal" onClick={(e) => e.stopPropagation()}>
          <div className="install-modal-title">Install SoKo</div>

          {env.inApp && (
            <div className="itip-note">
              <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /><path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              <span>You're viewing this inside another app. Open it in your <strong>browser</strong> first — tap the menu, then <strong>Open in browser</strong>.</span>
            </div>
          )}

          {variant === 'ios' ? <DemoIos /> : <DemoMenu />}

          {steps.map((s, i) => (
            <div className="install-modal-step" key={i}>
              <span className="install-modal-num">{i + 1}</span>
              <span>{s}</span>
            </div>
          ))}

          <button type="button" className="install-modal-close" onClick={handleDismiss}>
            Got it
          </button>
        </div>
      </div>
    );
  }

  // ── Banner ──
  if (!show) return null;

  return (
    <div className="install-banner">
      <div className="install-banner-content">
        <div className="install-banner-text">
          <div className="install-banner-title">Install SoKo</div>
          <div className="install-banner-desc">
            Add to your home screen for one-tap access.
          </div>
        </div>
        <div className="install-banner-actions">
          <button type="button" className="install-banner-dismiss" onClick={handleDismiss} aria-label="Dismiss">✕</button>
          <button type="button" className="install-banner-install" onClick={handleInstallClick}>Install</button>
        </div>
      </div>
    </div>
  );
}
